import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { format, addDays } from 'date-fns';
import { es } from 'date-fns/locale';
import { Check, CreditCard, Bitcoin, ArrowRight, ArrowLeft, Lock, Calendar as CalendarIcon, Clock, User, AlertCircle, Timer } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { useAuth } from '../context/AuthContext';
import api, { mensajeDeError, pesos } from '../lib/api';
import { CodigoContinuidad } from '../components/CodigoContinuidad';
import { toast } from 'sonner';

const LOCK_DURATION_SECONDS = 15 * 60; // el servidor bloquea el horario 15 minutos

const steps = [
  { id: 1, name: 'Profesional', description: 'Elige profesional y horario' },
  { id: 2, name: 'Datos', description: 'Tu seudónimo' },
  { id: 3, name: 'Pago', description: 'Confirma tu sesión' }
];

const AgendarPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { crearIdentidad, recuperarIdentidad, token, user } = useAuth();

  const [currentStep, setCurrentStep] = useState(1);
  const [professionals, setProfessionals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [bookingComplete, setBookingComplete] = useState(false);
  const [bookingData, setBookingData] = useState(null);

  // Código de continuidad de una identidad recién creada. Se muestra una vez.
  const [codigoNuevo, setCodigoNuevo] = useState(null);
  const [mostrarCodigo, setMostrarCodigo] = useState(false);

  // Dirección de la pasarela, guardada mientras se le enseña el código a quien
  // acaba de crear su identidad. Sin esta pausa el navegador salta a pagar y el
  // código se pierde con el estado de la página: en una plataforma anónima eso
  // significa perder el historial para siempre, porque no hay correo ni
  // contraseña con que recuperarlo.
  const [pagoPendiente, setPagoPendiente] = useState(null);
  const [codigoExistente, setCodigoExistente] = useState('');

  // Form state
  const [selectedProfessional, setSelectedProfessional] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [pseudonym, setPseudonym] = useState('');
  const [email, setEmail] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptConsent, setAcceptConsent] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('webpay');
  
  // Slot lock state
  const [lockId, setLockId] = useState(null);
  const [lockExpiresAt, setLockExpiresAt] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);

  useEffect(() => {
    const fetchProfessionals = async () => {
      try {
        const response = await api.get('/profesionales');
        setProfessionals(response.data);
        
        // Pre-select professional if passed in URL
        const preSelectedId = searchParams.get('profesional');
        if (preSelectedId) {
          const found = response.data.find(p => p.id === preSelectedId);
          if (found) setSelectedProfessional(found);
        }
      } catch (error) {
        console.error('Error fetching professionals:', error);
        toast.error('Error al cargar profesionales');
      } finally {
        setLoading(false);
      }
    };

    fetchProfessionals();
  }, [searchParams]);

  // La agenda de cada día la calcula el servidor a partir del horario semanal
  // del profesional, sus excepciones y lo que ya está tomado.
  useEffect(() => {
    const fetchAvailability = async () => {
      if (!selectedProfessional || !selectedDate) return;

      setLoadingSlots(true);
      try {
        const dateStr = format(selectedDate, 'yyyy-MM-dd');
        const { data } = await api.get(
          `/profesionales/${selectedProfessional.id}/disponibilidad`,
          { params: { date: dateStr } }
        );

        setAvailableSlots(Array.isArray(data) ? data : data.slots || []);
      } catch (error) {
        // Sin agenda no se puede elegir hora: mejor no ofrecer nada que
        // ofrecer horarios inventados que después van a fallar.
        setAvailableSlots([]);
        toast.error(mensajeDeError(error, 'No pudimos cargar los horarios'));
      } finally {
        setLoadingSlots(false);
      }
    };

    fetchAvailability();
  }, [selectedProfessional, selectedDate]);

  // Timer countdown for lock
  useEffect(() => {
    if (!lockExpiresAt) {
      setTimeRemaining(null);
      return;
    }

    const interval = setInterval(() => {
      const now = new Date();
      const expires = new Date(lockExpiresAt);
      const diff = Math.max(0, Math.floor((expires - now) / 1000));
      
      setTimeRemaining(diff);
      
      if (diff <= 0) {
        // Lock expired
        setLockId(null);
        setLockExpiresAt(null);
        setSelectedTime('');
        toast.error('El tiempo de reserva expiró. Selecciona el horario nuevamente.');
        setCurrentStep(1);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [lockExpiresAt]);

  /**
   * Al elegir la hora se bloquea el horario.
   *
   * Si aún no hay identidad anónima, se crea aquí con un seudónimo provisorio:
   * hace falta una sesión para poder bloquear. En el paso siguiente la persona
   * elige su seudónimo real y se actualiza, sin crear una identidad nueva.
   */
  const handleTimeSelect = async (time) => {
    if (!token) {
      const resultado = await crearIdentidad(null);
      if (!resultado.ok) {
        toast.error(resultado.error);
        return;
      }
      setCodigoNuevo(resultado.codigo);
    }

    try {
      const { data } = await api.post('/slots/lock', {
        psychologist_id: selectedProfessional.id,
        date: format(selectedDate, 'yyyy-MM-dd'),
        time,
      });

      setLockId(data.lock_id);
      setLockExpiresAt(data.expires_at);
      setSelectedTime(time);
      toast.success(data.message || 'Horario reservado mientras completas tu reserva');
    } catch (error) {
      toast.error(mensajeDeError(error, 'No pudimos reservar ese horario'));
      if (error?.response?.status === 409) {
        // El horario se tomó mientras decidía: se refresca la agenda.
        setSelectedDate(new Date(selectedDate));
      }
    }
  };

  /** Libera el horario si la persona se arrepiente. */
  const handleUnlockSlot = async () => {
    if (!lockId || !selectedTime) return;

    try {
      await api.post('/slots/unlock', {
        psychologist_id: selectedProfessional.id,
        date: format(selectedDate, 'yyyy-MM-dd'),
        time: selectedTime,
      });
    } catch {
      // Si falla, el bloqueo caduca solo en unos minutos.
    }

    setLockId(null);
    setLockExpiresAt(null);
    setSelectedTime('');
  };

  /** Entra con un código de continuidad existente, en vez de crear otra identidad. */
  const handleUsarCodigo = async () => {
    const resultado = await recuperarIdentidad(codigoExistente);

    if (!resultado.ok) {
      toast.error(resultado.error);
      return;
    }

    setPseudonym(resultado.data.pseudonym || '');
    setCodigoNuevo(null);
    setMostrarCodigo(false);
    toast.success(`Bienvenido de vuelta, ${resultado.data.pseudonym}`);
  };

  // Format time remaining
  const formatTimeRemaining = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleNextStep = async () => {
    if (currentStep === 1) {
      if (!selectedProfessional || !selectedDate || !selectedTime) {
        toast.error('Por favor selecciona profesional, fecha y hora');
        return;
      }
    }
    
    if (currentStep === 2) {
      if (!pseudonym.trim()) {
        toast.error('Por favor ingresa un seudónimo');
        return;
      }
      if (!acceptTerms || !acceptConsent) {
        toast.error('Debes aceptar los términos y el consentimiento informado');
        return;
      }

      // Se actualiza el seudónimo de la identidad que ya existe.
      // Crear otra dejaría al paciente con dos códigos distintos.
      try {
        await api.patch('/mi/perfil', { pseudonym });
      } catch (error) {
        toast.error(mensajeDeError(error, 'No pudimos guardar tu seudónimo'));
        return;
      }
    }

    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);

    try {
      // Se manda la fecha y la hora tal como se ven en la grilla, que son hora
      // de Chile, y son exactamente las mismas que se enviaron al bloquear.
      //
      // Antes se mandaba scheduledDate.toISOString(), que convierte a UTC con
      // el huso DEL NAVEGADOR. Desde fuera de Chile eso agendaba un horario
      // distinto del que la persona habia elegido y bloqueado, a veces sin
      // ningun error visible.
      const { data } = await api.post('/reservas', {
        psychologist_id: selectedProfessional.id,
        patient_email: email || null,
        date: format(selectedDate, 'yyyy-MM-dd'),
        time: selectedTime,
      });

      // Con pasarela real hay que ir a pagar fuera del sitio. El resultado
      // vuelve por /pago/resultado, que consulta el estado al servidor.
      if (data.payment?.checkout_url) {
        // Si la identidad se acaba de crear, primero se enseña el código: al
        // salir del sitio se pierde, y sin él la persona no puede volver nunca
        // a su historial.
        if (codigoNuevo) {
          setPagoPendiente(data.payment.checkout_url);
          setSubmitting(false);
          return;
        }
        window.location.href = data.payment.checkout_url;
        return;
      }

      setBookingData(data);
      setBookingComplete(true);

      if (data.payment_status === 'paid') {
        toast.success('¡Reserva confirmada!');
      } else {
        toast.success('Reserva creada. Falta completar el pago.');
      }
    } catch (error) {
      const estado = error?.response?.status;

      if (estado === 401) {
        toast.error('Tu sesión expiró. Vuelve a elegir el horario.');
        setCurrentStep(1);
      } else if (estado === 409) {
        toast.error(mensajeDeError(error, 'Ese horario ya no está disponible'));
        setCurrentStep(1);
      } else {
        toast.error(mensajeDeError(error, 'No pudimos crear la reserva'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Antes de salir a la pasarela: el código, y un botón para seguir.
  if (pagoPendiente && codigoNuevo) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-lg text-center">
          <h1 className="font-heading text-2xl font-semibold text-slate-900 mb-3">
            Guarda esto antes de pagar
          </h1>
          <p className="text-slate-600 mb-8 max-w-md mx-auto">
            Es la única forma de volver a tus sesiones. No te pedimos correo ni contraseña,
            así que si lo pierdes no podemos recuperarlo por ti.
          </p>

          <div className="mb-8 text-left">
            <CodigoContinuidad codigo={codigoNuevo} />
          </div>

          <Button
            onClick={() => { window.location.href = pagoPendiente; }}
            className="bg-teal-600 hover:bg-teal-700 text-white rounded-full px-8"
          >
            Ya lo guardé, ir a pagar
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>

          <p className="text-xs text-slate-500 mt-5">
            Tu horario queda reservado mientras completas el pago.
          </p>
        </div>
      </div>
    );
  }

  if (bookingComplete && bookingData) {
    return (
      <div className="min-h-screen bg-slate-50 py-16">
        <div className="container-max mx-auto px-4 md:px-8">
          <Card className="max-w-2xl mx-auto">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 rounded-full bg-sage-100 flex items-center justify-center mx-auto mb-6">
                <Check className="w-10 h-10 text-sage-600" />
              </div>
              
              <h1 className="font-heading text-3xl font-semibold text-slate-900 mb-4">
                ¡Sesión reservada!
              </h1>
              
              <p className="text-slate-600 mb-8">
                Tu sesión ha sido confirmada. Guarda estos datos para acceder a tu videollamada.
              </p>

              {/* Lo más importante de esta pantalla: sin el código, la persona
                  no puede volver a su historial ni gestionar su hora. */}
              {codigoNuevo && (
                <div className="mb-8 text-left">
                  <CodigoContinuidad codigo={codigoNuevo} />
                </div>
              )}

              <div className="bg-slate-50 rounded-2xl p-6 mb-8 text-left space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-slate-500">ID de reserva:</span>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm bg-white px-3 py-1 rounded border">{bookingData.id}</span>
                    <button 
                      onClick={() => {
                        navigator.clipboard.writeText(bookingData.id);
                        toast.success('ID copiado');
                      }}
                      className="text-teal-600 hover:text-teal-700 text-sm underline"
                    >
                      Copiar
                    </button>
                  </div>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Profesional:</span>
                  <span className="font-medium">{bookingData.psychologist_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Fecha:</span>
                  <span className="font-medium">
                    {format(new Date(bookingData.scheduled_date), "EEEE d 'de' MMMM, HH:mm", { locale: es })}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Tu seudónimo:</span>
                  <span className="font-medium">{bookingData.patient_pseudonym}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Estado:</span>
                  <Badge className="bg-sage-100 text-sage-700">Confirmada</Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Monto pagado:</span>
                  <span className="font-semibold text-teal-600">${bookingData.amount?.toLocaleString('es-CL')} CLP</span>
                </div>
              </div>

              <div className="p-4 bg-teal-50 rounded-xl border border-teal-200 mb-6">
                <p className="text-sm text-teal-800">
                  <strong>🔑 Guarda tu ID de reserva:</strong> Este código único identifica tu sesión. 
                  Úsalo para consultas sobre tu pago o si necesitas contactar soporte.
                  También está incluido en el enlace de tu sesión.
                </p>
              </div>

              <div className="space-y-4">
                <a 
                  href={bookingData.meeting_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="block"
                >
                  <Button className="w-full btn-primary" data-testid="join-meeting-btn">
                    Unirse a la sesión
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </a>
                
                <p className="text-sm text-slate-500">
                  El enlace estará activo 15 minutos antes de la hora programada.
                </p>
              </div>

              <div className="mt-8 p-4 bg-amber-50 rounded-xl border border-amber-200">
                <div className="flex gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                  <div className="text-left text-sm">
                    <p className="font-medium text-amber-800 mb-1">Importante</p>
                    <p className="text-amber-700">
                      {email
                        ? 'Te enviamos la confirmación a tu correo y te recordaremos la sesión un día antes.'
                        : 'No dejaste correo, así que no podremos enviarte recordatorios. Entra a "Mis horas" con tu código cuando quieras revisar tu sesión.'}
                    </p>
                  </div>
                </div>
              </div>

              <Link to="/mis-horas" className="inline-block mt-6 text-teal-700 underline text-sm">
                Ir a mis horas
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <section className="bg-teal-600 py-12">
        <div className="container-max mx-auto px-4 md:px-8">
          <div className="max-w-3xl">
            <Badge className="bg-white/20 text-white hover:bg-white/20 mb-4">
              <Lock className="w-3 h-3 mr-1" />
              Proceso 100% Anónimo
            </Badge>
            <h1 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-4">
              Agenda tu sesión
            </h1>
            <p className="text-teal-100">
              En 3 simples pasos, reserva tu sesión de psicología anónima.
            </p>
          </div>
        </div>
      </section>

      {/* Progress Steps */}
      <div className="bg-white border-b">
        <div className="container-max mx-auto px-4 md:px-8 py-6">
          <div className="flex justify-between max-w-2xl mx-auto">
            {steps.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm ${
                    currentStep > step.id 
                      ? 'bg-sage-600 text-white' 
                      : currentStep === step.id 
                        ? 'bg-teal-600 text-white' 
                        : 'bg-slate-100 text-slate-600'
                  }`}>
                    {currentStep > step.id ? <Check className="w-5 h-5" /> : step.id}
                  </div>
                  <p className={`mt-2 text-sm font-medium ${
                    currentStep >= step.id ? 'text-slate-900' : 'text-slate-500'
                  }`}>
                    {step.name}
                  </p>
                  <p className="text-xs text-slate-500 hidden sm:block">{step.description}</p>
                </div>
                {index < steps.length - 1 && (
                  <div className={`w-16 sm:w-24 h-0.5 mx-2 mt-[-20px] ${
                    currentStep > step.id ? 'bg-sage-600' : 'bg-slate-200'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Form Content */}
      <section className="section-padding">
        <div className="container-max mx-auto">
          <div className="max-w-4xl mx-auto">
            
            {/* Step 1: Select Professional and Time */}
            {currentStep === 1 && (
              <div className="space-y-8">
                <div>
                  <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-2">
                    Elige tu profesional
                  </h2>
                  <p className="text-slate-600">
                    Selecciona el psicólogo que mejor se adapte a tus necesidades.
                  </p>
                </div>

                {loading ? (
                  <div className="text-center py-8">Cargando profesionales...</div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-4">
                    {professionals.map((pro) => (
                      <Card 
                        key={pro.id}
                        className={`cursor-pointer transition-all duration-200 ${
                          selectedProfessional?.id === pro.id 
                            ? 'ring-2 ring-teal-600 bg-teal-50' 
                            : 'hover:shadow-md'
                        }`}
                        onClick={() => setSelectedProfessional(pro)}
                        data-testid={`select-professional-${pro.id}`}
                      >
                        <CardContent className="p-4 flex gap-4">
                          <img 
                            src={pro.profile_image} 
                            alt={pro.name}
                            className="w-20 h-20 rounded-xl object-cover"
                          />
                          <div className="flex-1">
                            <h3 className="font-semibold text-slate-900">{pro.name}</h3>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {pro.specialties.slice(0, 2).map((s) => (
                                <Badge key={s} variant="secondary" className="text-xs">
                                  {s}
                                </Badge>
                              ))}
                            </div>
                            <p className="text-sm text-slate-600 mt-2">
                              Desde ${pro.price_from.toLocaleString('es-CL')} CLP
                            </p>
                          </div>
                          {selectedProfessional?.id === pro.id && (
                            <Check className="w-6 h-6 text-teal-600" />
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}

                {selectedProfessional && (
                  <div className="grid md:grid-cols-2 gap-8 pt-8 border-t">
                    <div>
                      <h3 className="font-heading text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                        <CalendarIcon className="w-5 h-5 text-teal-600" />
                        Selecciona fecha
                      </h3>
                      <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={setSelectedDate}
                        disabled={(date) => date < new Date() || date > addDays(new Date(), 30)}
                        locale={es}
                        className="rounded-xl border"
                        data-testid="booking-calendar"
                      />
                    </div>

                    <div>
                      <h3 className="font-heading text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                        <Clock className="w-5 h-5 text-teal-600" />
                        Selecciona hora
                      </h3>
                      
                      {/* Mensaje si no se ha seleccionado fecha */}
                      {!selectedDate && (
                        <div className="p-4 bg-slate-100 rounded-xl border border-slate-200 text-center">
                          <CalendarIcon className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                          <p className="text-slate-600 font-medium">Primero selecciona una fecha</p>
                          <p className="text-sm text-slate-500">Haz clic en el calendario para ver los horarios disponibles</p>
                        </div>
                      )}
                      
                      {/* Horarios - solo visibles cuando hay fecha seleccionada */}
                      {selectedDate && (
                        <>
                          {loadingSlots && (
                            <p className="text-sm text-slate-500 py-4">Cargando horarios…</p>
                          )}

                          {/* Los horarios los define el profesional en su agenda:
                              aquí solo se muestra lo que el servidor ofrece. */}
                          {!loadingSlots && availableSlots.length === 0 && (
                            <div className="text-center py-8 px-4 bg-slate-50 rounded-xl">
                              <CalendarIcon className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                              <p className="text-slate-600 font-medium">
                                Sin horarios para este día
                              </p>
                              <p className="text-sm text-slate-500">
                                Este profesional no atiende esa fecha. Prueba con otro día.
                              </p>
                            </div>
                          )}

                          {!loadingSlots && availableSlots.length > 0 && (
                            <div className="grid grid-cols-3 gap-2">
                              {availableSlots.map((slot) => {
                                const isBooked = slot.booked === true;
                                const isLocked = slot.locked === true && selectedTime !== slot.time;
                                const isTooSoon = slot.too_soon === true;
                                const isDisabled = !slot.available && selectedTime !== slot.time;

                                return (
                                  <Button
                                    key={slot.time}
                                    variant={selectedTime === slot.time ? 'default' : 'outline'}
                                    className={`relative ${selectedTime === slot.time ? 'bg-teal-600 hover:bg-teal-700' : ''} ${
                                      isBooked
                                        ? 'bg-red-50 border-red-200 text-red-400 hover:bg-red-50 cursor-not-allowed line-through'
                                        : isLocked
                                          ? 'bg-amber-50 border-amber-200 text-amber-600 hover:bg-amber-50 cursor-not-allowed'
                                          : isTooSoon
                                            ? 'bg-slate-50 border-slate-200 text-slate-400 cursor-not-allowed'
                                            : ''
                                    }`}
                                    onClick={() => !isDisabled && handleTimeSelect(slot.time)}
                                    disabled={isDisabled}
                                    title={isTooSoon ? 'Muy cerca de la hora actual' : undefined}
                                    data-testid={`time-slot-${slot.time}`}
                                  >
                                    {slot.time}
                                    {isBooked && (
                                      <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full"></span>
                                    )}
                                    {isLocked && !isBooked && <Lock className="w-3 h-3 ml-1 inline" />}
                                  </Button>
                                );
                              })}
                            </div>
                          )}
                          
                          {/* Leyenda de estados */}
                          <div className="flex flex-wrap gap-4 mt-4 text-xs">
                            <div className="flex items-center gap-1">
                              <div className="w-3 h-3 rounded border-2 border-slate-300 bg-white"></div>
                              <span className="text-slate-600">Disponible</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <div className="w-3 h-3 rounded bg-red-500"></div>
                              <span className="text-slate-600">Ocupado</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <div className="w-3 h-3 rounded bg-amber-400"></div>
                              <span className="text-slate-600">Reservando</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <div className="w-3 h-3 rounded bg-teal-600"></div>
                              <span className="text-slate-600">Tu selección</span>
                            </div>
                          </div>
                        </>
                      )}
                      
                      {/* Timer display */}
                      {timeRemaining !== null && timeRemaining > 0 && (
                        <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                          <div className="flex items-center gap-3">
                            <Timer className="w-5 h-5 text-amber-600" />
                            <div>
                              <p className="font-medium text-amber-900">
                                Horario reservado temporalmente
                              </p>
                              <p className="text-sm text-amber-700">
                                Tienes <strong>{formatTimeRemaining(timeRemaining)}</strong> para completar el pago
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Step 2: Pseudonym and Consent */}
            {currentStep === 2 && (
              <div className="max-w-xl mx-auto space-y-8">
                <div>
                  <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-2">
                    Tu identidad anónima
                  </h2>
                  <p className="text-slate-600">
                    Elige un seudónimo para tu sesión. No necesitamos tu nombre real.
                  </p>
                </div>

                {/* Quien ya vino antes usa su código y conserva su historial
                    con el mismo profesional, en vez de empezar de cero. */}
                <Card className="border-teal-200 bg-teal-50/40">
                  <CardContent className="p-5">
                    {!mostrarCodigo ? (
                      <button
                        type="button"
                        onClick={() => setMostrarCodigo(true)}
                        className="text-sm text-teal-800 underline"
                        data-testid="ya-tengo-codigo"
                      >
                        Ya vine antes y tengo un código de continuidad
                      </button>
                    ) : (
                      <div className="space-y-3">
                        <Label htmlFor="codigo-existente" className="text-teal-900">
                          Tu código de continuidad
                        </Label>
                        <div className="flex flex-col sm:flex-row gap-2">
                          <Input
                            id="codigo-existente"
                            placeholder="ABCD-2345-EFGH"
                            value={codigoExistente}
                            onChange={(e) => setCodigoExistente(e.target.value.toUpperCase())}
                            className="h-11 font-mono tracking-widest"
                            autoComplete="off"
                            spellCheck="false"
                          />
                          <Button
                            type="button"
                            onClick={handleUsarCodigo}
                            disabled={codigoExistente.trim().length < 12}
                            className="bg-teal-600 hover:bg-teal-700 text-white h-11 px-6"
                          >
                            Usar
                          </Button>
                        </div>
                        <button
                          type="button"
                          onClick={() => setMostrarCodigo(false)}
                          className="text-xs text-slate-500 underline"
                        >
                          Prefiero empezar de nuevo
                        </button>
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6 space-y-6">
                    <div className="space-y-2">
                      <Label htmlFor="pseudonym" className="flex items-center gap-2">
                        <User className="w-4 h-4" />
                        Seudónimo *
                      </Label>
                      <Input
                        id="pseudonym"
                        placeholder="Ej: Luna, Viajero123, Anónimo..."
                        value={pseudonym}
                        onChange={(e) => setPseudonym(e.target.value)}
                        className="h-12"
                        data-testid="input-pseudonym"
                      />
                      <p className="text-xs text-slate-500">
                        Este nombre será visible para tu psicólogo durante la sesión.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email">
                        Email <span className="text-slate-400">(opcional)</span>
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="tu@email.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="h-12"
                        data-testid="input-email"
                      />
                      <p className="text-xs text-slate-500">
                        Solo para recordatorios. No es obligatorio.
                      </p>
                      <div className="p-3 bg-teal-50 rounded-lg border border-teal-100">
                        <p className="text-xs text-teal-700">
                          <strong>💡 Tip de privacidad:</strong> Si no quieres usar tu email real, 
                          puedes usar un email temporal como{' '}
                          <a href="https://yopmail.com" target="_blank" rel="noopener noreferrer" className="underline font-medium">yopmail.com</a>,{' '}
                          <a href="https://temp-mail.org" target="_blank" rel="noopener noreferrer" className="underline font-medium">temp-mail.org</a> o{' '}
                          <a href="https://tmailor.com" target="_blank" rel="noopener noreferrer" className="underline font-medium">tmailor.com</a>
                        </p>
                      </div>
                    </div>

                    <div className="space-y-4 pt-4 border-t">
                      <div className="flex items-start gap-3">
                        <Checkbox 
                          id="terms" 
                          checked={acceptTerms}
                          onCheckedChange={setAcceptTerms}
                          data-testid="checkbox-terms"
                        />
                        <Label htmlFor="terms" className="text-sm text-slate-600 leading-relaxed cursor-pointer">
                          He leído y acepto los{' '}
                          <a href="/legales/terminos" target="_blank" className="text-teal-600 hover:underline">
                            Términos y Condiciones
                          </a>{' '}
                          y la{' '}
                          <a href="/legales/privacidad" target="_blank" className="text-teal-600 hover:underline">
                            Política de Privacidad
                          </a>
                        </Label>
                      </div>

                      <div className="flex items-start gap-3">
                        <Checkbox 
                          id="consent" 
                          checked={acceptConsent}
                          onCheckedChange={setAcceptConsent}
                          data-testid="checkbox-consent"
                        />
                        <Label htmlFor="consent" className="text-sm text-slate-600 leading-relaxed cursor-pointer">
                          He leído y acepto el{' '}
                          <a href="/legales/consentimiento" target="_blank" className="text-teal-600 hover:underline">
                            Consentimiento Informado
                          </a>{' '}
                          para telepsicología
                        </Label>
                      </div>
                    </div>

                    <div className="privacy-badge mt-4">
                      <Lock className="w-4 h-4" />
                      Tus datos están protegidos y encriptados
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Step 3: Payment */}
            {currentStep === 3 && (
              <div className="max-w-xl mx-auto space-y-8">
                <div>
                  <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-2">
                    Confirma tu sesión
                  </h2>
                  <p className="text-slate-600">
                    Revisa los detalles y selecciona tu método de pago.
                  </p>
                </div>

                {/* Summary */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Resumen de tu reserva</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Profesional:</span>
                      <span className="font-medium">{selectedProfessional?.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Fecha:</span>
                      <span className="font-medium">
                        {selectedDate && format(selectedDate, "EEEE d 'de' MMMM", { locale: es })}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Hora:</span>
                      <span className="font-medium">{selectedTime} hrs</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Tu seudónimo:</span>
                      <span className="font-medium">{pseudonym}</span>
                    </div>
                    <hr />
                    <div className="flex justify-between text-lg">
                      <span className="font-medium">Total:</span>
                      <span className="font-bold text-teal-600">
                        ${selectedProfessional?.price_from.toLocaleString('es-CL')} CLP
                      </span>
                    </div>
                  </CardContent>
                </Card>

                {/* Payment Methods */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Método de pago</CardTitle>
                    <CardDescription>
                      Selecciona cómo deseas pagar tu sesión
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod} className="space-y-3">
                      <div 
                        className={`payment-method ${paymentMethod === 'webpay' ? 'selected' : ''}`}
                        onClick={() => setPaymentMethod('webpay')}
                      >
                        <div className="flex items-center gap-4">
                          <RadioGroupItem value="webpay" id="webpay" />
                          <CreditCard className="w-6 h-6 text-slate-600" />
                          <div className="flex-1">
                            <Label htmlFor="webpay" className="font-medium cursor-pointer">
                              Webpay / Tarjeta
                            </Label>
                            <p className="text-sm text-slate-500">Débito o crédito</p>
                          </div>
                        </div>
                      </div>

                      <div 
                        className={`payment-method ${paymentMethod === 'flow' ? 'selected' : ''}`}
                        onClick={() => setPaymentMethod('flow')}
                      >
                        <div className="flex items-center gap-4">
                          <RadioGroupItem value="flow" id="flow" />
                          <CreditCard className="w-6 h-6 text-slate-600" />
                          <div className="flex-1">
                            <Label htmlFor="flow" className="font-medium cursor-pointer">
                              Flow / Khipu
                            </Label>
                            <p className="text-sm text-slate-500">Transferencia bancaria</p>
                          </div>
                        </div>
                      </div>

                      <div 
                        className={`payment-method ${paymentMethod === 'bitcoin' ? 'selected' : ''}`}
                        onClick={() => setPaymentMethod('bitcoin')}
                      >
                        <div className="flex items-center gap-4">
                          <RadioGroupItem value="bitcoin" id="bitcoin" />
                          <Bitcoin className="w-6 h-6 text-amber-500" />
                          <div className="flex-1">
                            <Label htmlFor="bitcoin" className="font-medium cursor-pointer">
                              Bitcoin
                            </Label>
                            <p className="text-sm text-slate-500">Via BTCPay Server (máxima privacidad)</p>
                          </div>
                        </div>
                      </div>
                    </RadioGroup>

                    <div className="mt-6 p-4 bg-slate-50 rounded-xl">
                      <p className="text-sm text-slate-600">
                        <strong>Nota:</strong> Se emitirá boleta electrónica independientemente del método de pago seleccionado.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-12 pt-8 border-t">
              <Button
                variant="outline"
                onClick={handlePreviousStep}
                disabled={currentStep === 1}
                className="gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Anterior
              </Button>

              {currentStep < 3 ? (
                <Button 
                  onClick={handleNextStep}
                  className="btn-primary gap-2"
                  data-testid="next-step-btn"
                >
                  Siguiente
                  <ArrowRight className="w-4 h-4" />
                </Button>
              ) : (
                <Button 
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="btn-primary gap-2"
                  data-testid="confirm-booking-btn"
                >
                  {submitting ? 'Procesando...' : 'Confirmar y Pagar'}
                  <ArrowRight className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AgendarPage;
